import { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldOff, X, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Isolamento di rete di un endpoint dal SOC (backend routes/edrActions.ts, 2026-10-30). Oggi solo
// per gli incidenti EDR Acronis: il vendor lega l'azione all'incidente. Solo admin e analyst; se
// l'asset è business critical il modulo chiede che il cliente sia stato contattato, chi ha
// autorizzato e come. Lo storico delle azioni dell'alert resta visibile a tutti.

interface Availability { isolate: boolean; businessCritical: boolean; unknownAsset: boolean; confirmationRequired: boolean; host: string }

interface ResponseAction {
  id: number;
  action: string;
  host: string;
  reason: string;
  business_critical: boolean;
  customer_contacted: boolean | null;
  authorized_by: string | null;
  authorization_channel: 'email' | 'phone' | null;
  status: 'requested' | 'sent' | 'failed';
  vendor_state: string | null;
  error: string | null;
  created_at: string;
  requested_by_name: string | null;
}

const STATUS_BADGE: Record<ResponseAction['status'], string> = {
  requested: 'bg-gray-100 text-gray-600', sent: 'bg-emerald-100 text-emerald-700', failed: 'bg-red-100 text-red-700',
};

export default function EdrResponsePanel({ alertId, onDone }: { alertId: number; onDone?: () => void }) {
  const { canEdit } = useAuth();
  const [availability, setAvailability] = useState<Availability | null>(null);
  const [actions, setActions] = useState<ResponseAction[]>([]);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [contacted, setContacted] = useState(false);
  const [authorizedBy, setAuthorizedBy] = useState('');
  const [channel, setChannel] = useState<'' | 'email' | 'phone'>('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    const [a, list] = await Promise.all([
      axios.get('/api/edr-actions/availability', { params: { alertId } }).catch(() => null),
      axios.get('/api/edr-actions', { params: { alertId } }).catch(() => null),
    ]);
    setAvailability(a ? a.data.data : null);
    setActions(list ? list.data.data : []);
  }

  useEffect(() => { load(); }, [alertId]);

  if (!availability?.isolate && actions.length === 0) return null;

  const critical = !!availability?.confirmationRequired;
  const ready = reason.trim().length >= 5 && (!critical || (contacted && authorizedBy.trim().length >= 2 && channel !== ''));

  async function submit() {
    setSending(true);
    setError(null);
    try {
      await axios.post('/api/edr-actions/isolate', {
        alertId, reason: reason.trim(),
        customerContacted: critical ? contacted : contacted || undefined,
        authorizedBy: authorizedBy.trim() || undefined,
        authorizationChannel: channel || undefined,
      });
      setOpen(false);
      setReason(''); setContacted(false); setAuthorizedBy(''); setChannel('');
      await load();
      onDone?.();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'The isolation request failed');
      await load();
    } finally {
      setSending(false);
    }
  }

  async function refresh(id: number) {
    await axios.post(`/api/edr-actions/${id}/refresh`).catch(() => null);
    load();
  }

  return (
    <section className="rounded-lg border border-red-100 bg-red-50/40 p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 inline-flex items-center gap-2"><ShieldOff size={15} className="text-red-600" /> Response: network isolation</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Asks Acronis to cut {availability?.host || 'the endpoint'} off the network (it stays reachable only from the Acronis console).
            Acronis first accepts the request, then executes it: use "status" to confirm. Removing the isolation is done from the Acronis console.
          </p>
        </div>
        {availability?.isolate && canEdit && (
          <button onClick={() => { setOpen(true); setError(null); }} className="btn-primary bg-red-600 hover:bg-red-700 text-sm">Isolate machine…</button>
        )}
      </div>

      {actions.length > 0 && (
        <ul className="mt-3 divide-y divide-red-100 text-xs">
          {actions.map(a => (
            <li key={a.id} className="py-2 flex items-start gap-2 flex-wrap">
              <span className={`badge ${STATUS_BADGE[a.status]}`}>{a.status}{a.vendor_state ? ` · ${a.vendor_state}` : ''}</span>
              <span className="text-gray-700">
                {new Date(a.created_at).toLocaleString('en-GB')} by {a.requested_by_name || 'unknown'} — {a.reason}
                {a.authorized_by && ` · authorised by ${a.authorized_by}${a.authorization_channel ? ` (${a.authorization_channel})` : ''}`}
                {a.error && <span className="text-red-700"> · {a.error}</span>}
              </span>
              {a.status === 'sent' && canEdit && (
                <button onClick={() => refresh(a.id)} className="ml-auto text-primary-600 hover:underline inline-flex items-center gap-1"><RefreshCw size={11} /> status</button>
              )}
            </li>
          ))}
        </ul>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Isolate {availability?.host} from the network</h2>
              <button onClick={() => setOpen(false)} aria-label="Close"><X size={16} /></button>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              The machine will stop working on the customer network until someone removes the isolation from the Acronis console.
            </p>
            {critical && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded p-2 mt-3">
                {availability?.unknownAsset
                  ? <>This machine is <span className="font-semibold">not in the asset inventory</span>, so it is treated as critical: isolate it only after the customer has been contacted and has authorised it.</>
                  : <>This is a <span className="font-semibold">business critical asset</span>: isolate it only after the customer has been contacted and has authorised it.</>}
              </p>
            )}
            <label className="block text-xs text-gray-500 mt-3">Reason (required)</label>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} maxLength={500} className="input-field w-full text-sm" placeholder="e.g. ransomware behaviour confirmed, lateral movement in progress" />
            {critical && (
              <div className="mt-3 space-y-2">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={contacted} onChange={e => setContacted(e.target.checked)} /> The customer has been contacted</label>
                <div>
                  <label className="block text-xs text-gray-500">Who authorised it at the customer</label>
                  <input value={authorizedBy} onChange={e => setAuthorizedBy(e.target.value)} maxLength={120} className="input-field w-full text-sm" placeholder="Name and role" />
                </div>
                <div>
                  <span className="block text-xs text-gray-500">How</span>
                  <div className="flex gap-4 text-sm mt-1">
                    <label className="flex items-center gap-1.5"><input type="radio" name="channel" checked={channel === 'email'} onChange={() => setChannel('email')} /> Email</label>
                    <label className="flex items-center gap-1.5"><input type="radio" name="channel" checked={channel === 'phone'} onChange={() => setChannel('phone')} /> Phone</label>
                  </div>
                </div>
              </div>
            )}
            {error && <p className="text-sm text-red-700 mt-3">{error}</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setOpen(false)} className="btn-secondary">Cancel</button>
              <button onClick={submit} disabled={!ready || sending} className="btn-primary bg-red-600 hover:bg-red-700 disabled:opacity-50">
                {sending ? 'Sending…' : 'Isolate now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
